/**
 * CookiesPage - Cookie Policy
 *
 * SEO Target: /cookies
 */

import { Link } from "react-router-dom";
import { PageMeta, getBreadcrumbSchema, SEO_CONFIG } from "@/lib/seo";
import { Breadcrumbs } from "@/layouts/PublicLayout";
import { Helmet } from "react-helmet-async";
import { Cookie, ChevronRight, Check } from "lucide-react";
import { useCompanyInfo } from "@/hooks/use-company-info";

export function CookiesPage() {
  const { company } = useCompanyInfo();
  const lastUpdated = "2026-02-01";

  const breadcrumbItems = [{ label: "Cookies" }];

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: "Hem", url: "/" },
    { name: "Cookies", url: "/cookies" },
  ]);

  const sections = [
    { id: "vad-ar-cookies", title: "Vad är cookies?" },
    { id: "nodvandiga", title: "Nödvändiga cookies" },
    { id: "funktionella", title: "Funktionella cookies" },
    { id: "analytiska", title: "Analytiska cookies" },
    { id: "hantera", title: "Hantera cookies" },
    { id: "tredjeparter", title: "Tredjepartscookies" },
    { id: "andringar", title: "Ändringar" },
    { id: "kontakt", title: "Kontakt" },
  ];

  return (
    <>
      <PageMeta
        title="Cookie Policy - Kvanten"
        description="Information om hur Kvanten använder cookies på webbplatsen. Läs om vilka cookies vi använder och hur du kan hantera dem."
        canonicalUrl={`${SEO_CONFIG.siteUrl}/cookies`}
        fullTitle
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Cookie className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">Cookie Policy</h1>
              <p className="text-gray-500">Senast uppdaterad: {lastUpdated}</p>
            </div>
          </div>

          <p className="text-lg text-gray-600">
            Denna cookie policy förklarar hur Kvanten använder cookies och liknande teknologier
            på vår webbplats kvanten.se.
          </p>
        </div>
      </section>

      {/* Content with sidebar */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-4 lg:gap-12">
            {/* Table of contents - Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 bg-gray-50 rounded-xl p-6">
                <h2 className="font-semibold text-gray-900 mb-4">Innehåll</h2>
                <nav className="space-y-2">
                  {sections.map((section) => (
                    <a
                      key={section.id}
                      href={`#${section.id}`}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors py-1"
                    >
                      <ChevronRight className="w-3 h-3" />
                      {section.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <article className="lg:col-span-3 prose prose-gray max-w-none">
              <section id="vad-ar-cookies">
                <h2>Vad är cookies?</h2>
                <p>
                  Cookies är små textfiler som lagras på din enhet (dator, mobiltelefon eller
                  surfplatta) när du besöker en webbplats. De används för att webbplatsen ska
                  fungera korrekt, för att komma ihåg dina preferenser och för att förbättra din
                  upplevelse.
                </p>

                {/* Cookie overview cards */}
                <div className="grid md:grid-cols-3 gap-4 not-prose my-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-green-900">Nödvändiga</span>
                    </div>
                    <p className="text-sm text-green-700">Krävs för grundläggande funktionalitet</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-blue-900">Funktionella</span>
                    </div>
                    <p className="text-sm text-blue-700">Förbättrar din upplevelse</p>
                  </div>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-purple-900">Analytiska</span>
                    </div>
                    <p className="text-sm text-purple-700">Anonymiserad statistik</p>
                  </div>
                </div>
              </section>

              <section id="nodvandiga">
                <h2>Nödvändiga cookies</h2>
                <p>
                  Dessa cookies krävs för att webbplatsen ska fungera och kan inte stängas av. De
                  används för att hantera inloggning, säkerhet och grundläggande funktionalitet.
                </p>
                <div className="overflow-x-auto not-prose">
                  <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Cookie</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Syfte</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">
                          Lagringstid
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-3 px-4 font-mono text-sm text-gray-700">
                          kvanten_session
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          Sessionshantering för inloggade användare
                        </td>
                        <td className="py-3 px-4 text-gray-600">Session</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-mono text-sm text-gray-700">kvanten_csrf</td>
                        <td className="py-3 px-4 text-gray-600">CSRF-skydd för säkerhet</td>
                        <td className="py-3 px-4 text-gray-600">Session</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-mono text-sm text-gray-700">kvanten_auth</td>
                        <td className="py-3 px-4 text-gray-600">Autentiseringstoken</td>
                        <td className="py-3 px-4 text-gray-600">24 timmar</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="funktionella">
                <h2>Funktionella cookies</h2>
                <p>
                  Dessa cookies används för att komma ihåg dina val och preferenser, som språk och
                  visningsinställningar.
                </p>
                <div className="overflow-x-auto not-prose">
                  <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Cookie</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Syfte</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">
                          Lagringstid
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-3 px-4 font-mono text-sm text-gray-700">kvanten_prefs</td>
                        <td className="py-3 px-4 text-gray-600">Användarpreferenser</td>
                        <td className="py-3 px-4 text-gray-600">1 år</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="analytiska">
                <h2>Analytiska cookies</h2>
                <p>
                  Dessa cookies hjälper oss att förstå hur besökare använder webbplatsen genom att
                  samla in anonymiserad statistik.
                </p>
                <div className="overflow-x-auto not-prose">
                  <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Cookie</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Syfte</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">
                          Lagringstid
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      <tr>
                        <td className="py-3 px-4 font-mono text-sm text-gray-700">_ga</td>
                        <td className="py-3 px-4 text-gray-600">
                          Google Analytics - särskilja användare
                        </td>
                        <td className="py-3 px-4 text-gray-600">2 år</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-mono text-sm text-gray-700">_gid</td>
                        <td className="py-3 px-4 text-gray-600">
                          Google Analytics - särskilja användare
                        </td>
                        <td className="py-3 px-4 text-gray-600">24 timmar</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>

              <section id="hantera">
                <h2>Hur kan du hantera cookies?</h2>
                <p>Du kan hantera cookies på flera sätt:</p>

                <h3>Via webbläsaren</h3>
                <p>
                  De flesta webbläsare låter dig blockera eller radera cookies via inställningarna.
                  Observera att om du blockerar nödvändiga cookies kan vissa funktioner på
                  webbplatsen sluta fungera.
                </p>

                <div className="grid md:grid-cols-2 gap-4 not-prose my-6">
                  {[
                    {
                      browser: "Chrome",
                      path: "Inställningar → Sekretess och säkerhet → Cookies",
                    },
                    { browser: "Firefox", path: "Inställningar → Sekretess och säkerhet → Cookies" },
                    { browser: "Safari", path: "Inställningar → Sekretess → Cookies" },
                    { browser: "Edge", path: "Inställningar → Cookies och webbplatsdata" },
                  ].map((item, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <p className="font-semibold text-gray-900">{item.browser}</p>
                      <p className="text-sm text-gray-600 mt-1">{item.path}</p>
                    </div>
                  ))}
                </div>

                <h3>Via samtyckesbannern</h3>
                <p>
                  När du besöker vår webbplats för första gången visas en banner där du kan välja
                  vilka typer av cookies du accepterar. Du kan ändra ditt val när som helst genom
                  att klicka på "Cookie-inställningar" längst ner på sidan.
                </p>
              </section>

              <section id="tredjeparter">
                <h2>Tredjepartscookies</h2>
                <p>
                  Vi använder tjänster från tredje part som kan sätta egna cookies. Dessa
                  leverantörer har egna integritetspolicyer:
                </p>

                <div className="not-prose my-6 space-y-3">
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                    <div>
                      <p className="font-semibold text-gray-900">Google Analytics</p>
                      <p className="text-sm text-gray-600">Anonymiserad trafikstatistik</p>
                    </div>
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      Privacy Policy →
                    </a>
                  </div>
                  <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                    <div>
                      <p className="font-semibold text-gray-900">Stripe</p>
                      <p className="text-sm text-gray-600">Betalningshantering</p>
                    </div>
                    <a
                      href="https://stripe.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline text-sm"
                    >
                      Privacy Policy →
                    </a>
                  </div>
                </div>
              </section>

              <section id="andringar">
                <h2>Ändringar</h2>
                <p>
                  Vi kan uppdatera denna cookie policy från tid till annan. Den senaste versionen
                  finns alltid tillgänglig på denna sida.
                </p>
              </section>

              <section id="kontakt">
                <h2>Kontakt</h2>
                <p>Om du har frågor om vår användning av cookies, kontakta oss:</p>
                <div className="bg-gray-50 rounded-lg p-4 not-prose">
                  <p className="text-gray-700">
                    <strong>E-post:</strong>{" "}
                    <a href={`mailto:${company.email}`} className="text-blue-600 hover:underline">
                      {company.email}
                    </a>
                  </p>
                </div>
              </section>

              <hr className="my-8" />

              <div className="bg-blue-50 rounded-lg p-4 not-prose">
                <p className="text-sm text-blue-800">
                  Se även vår{" "}
                  <Link to="/integritetspolicy" className="text-blue-600 hover:underline">
                    Integritetspolicy
                  </Link>{" "}
                  för mer information om hur vi hanterar dina personuppgifter.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}
